import { Icon } from "@iconify/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ContactStore } from "../../../assets/store/contact.store";
import { SocialsStore } from "../../../assets/store/social.store";
import { Theme } from "../../themes/theme";
import DevelopedByChip from "../chip/DevelopedByChip";
import Logo from "../logo/Logo";

const { color, font, border } = Theme;

const sitemap = [
  { name: "Home", identifier: ["", "/", "/home"], href: "/" },
  { name: "Service", identifier: ["services"], href: "/services" },
  { name: "About", identifier: ["about"], href: "/about" },
  { name: "Projects", identifier: ["projects"], href: "/projects" },
];

const support = [
  { name: "FAQs", identifier: ["faqs"], href: "/faqs" },
  { name: "Contact", identifier: ["contact"], href: "/contact" },
];

const bottomLinks = ["Privacy Policy", "Terms of Use", "NDA Request"];

const Wrapper = styled.footer`
  font-family: ${font.fontFamily.sans};
`;

const Top = styled.div`
  background: ${color.surface.light};
  border-bottom: 1px solid ${color.border.light};
  padding: 64px 50px 56px;
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr 1fr;
  gap: 48px;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    grid-template-columns: 1fr 1fr;
    padding: 50px 20px;
    gap: 36px;
  }

  @media (max-width: ${Theme.breakpoints.MOBILE}px) {
    grid-template-columns: 1fr;
    padding: 48px 28px 40px;
  }
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const Tagline = styled.p`
  font-size: ${font.fontSize.sm};
  color: ${color.text.secondary};
  line-height: ${font.lineHeight.relaxed};
  margin: 0 0 22px;
  max-width: 240px;

  @media (max-width: 560px) {
    max-width: 100%;
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SocialBtn = styled.div`
  width: 32px;
  height: 32px;
  background: ${color.background.light};
  /* border: 1px solid ${color.border.light}; */
  border: none;
  border-radius: ${border.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color.text.secondary};
  cursor: pointer;

  &:hover {
    border-color: ${color.text.primary};
    color: ${color.text.inverse};
    img {
      max-height: 30px;
    }
  }
  img {
    max-height: 28px;
    transition-duration: 0.2s;
  }
`;

const ColTitle = styled.h4`
  font-family: ${font.fontFamily.heading[0]}, sans-serif;
  font-size: ${font.fontSize.sm};
  font-weight: ${font.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: ${font.letterSpacing.widest};
  color: ${color.text.heading};
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LinkList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LinkItem = styled.li`
  font-size: ${font.fontSize.base};
  color: ${color.text.secondary};
  text-decoration: none;
  transition: color 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  &:hover {
    color: ${color.text.heading};
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
    color: ${color.text.primary};
  }
`;

const ContactText = styled.div`
  font-size: ${font.fontSize.base};
  color: ${color.text.secondary};
  line-height: ${font.lineHeight.relaxed};

  a {
    color: ${color.text.secondary};
    text-decoration: none;
    transition: color 0.15s;

    &:hover {
      color: ${color.text.heading};
    }
  }
`;

const Bottom = styled.div`
  background-color: ${Theme.color.text.primary};
  padding: 20px 50px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    grid-template-columns: 1fr;
    padding: 24px 28px;
    gap: 14px;
  }
`;

const Copy = styled.p`
  font-size: ${font.fontSize.sm};
  color: ${Theme.color.text.inverse};
  letter-spacing: ${font.letterSpacing.wide};
  margin: 0;
  white-space: nowrap;

  span {
    color: rgba(255, 255, 255, 0.6);
  }

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    order: 2;
  }
`;

const BottomLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;

  a {
    font-size: ${font.fontSize.sm};
    color: ${Theme.color.text.inverse};
    text-decoration: none;
    letter-spacing: ${font.letterSpacing.wide};
    transition: opacity 0.15s;
    white-space: nowrap;
    line-height: 1;

    &:hover {
      opacity: 0.7;
    }
  }

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    justify-content: flex-start;
    order: 1;
    gap: 12px;
  }
`;

const Sep = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(255, 255, 255, 0.25);
  flex-shrink: 0;
`;

const Right = styled.div`
  white-space: nowrap;
  align-self: right;
  width: fit-content;
  margin-left: auto;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    align-self: left;
    margin-right: auto;
    order: 3;
  }
`;

function Footer() {
  const navigate = useNavigate();

  return (
    <Wrapper>
      <Top>
        {/* Brand */}
        <div>
          <LogoRow>
            <Logo height="60px" />
          </LogoRow>
          <Tagline>
            Precision CNC machining and metal components for industries where
            accuracy is non-negotiable. ISO certified. Globally trusted.
          </Tagline>
          <SocialRow>
            {SocialsStore.map((s) => (
              <SocialBtn key={s.label} title={s.label}>
                <img src={s.icon} alt={s.label} />
              </SocialBtn>
            ))}
          </SocialRow>
        </div>

        {/* Company */}
        <div>
          <ColTitle>Company</ColTitle>
          <LinkList>
            {sitemap.map((item, key) => (
              <LinkItem key={key} onClick={() => navigate(item.href)}>
                {item.name}
              </LinkItem>
            ))}
          </LinkList>
        </div>

        {/* Support */}
        <div>
          <ColTitle>Support</ColTitle>
          <LinkList>
            {support.map((item, key) => (
              <LinkItem key={key} onClick={() => navigate(item.href)}>
                {item.name}
              </LinkItem>
            ))}
          </LinkList>
        </div>

        {/* Contact */}
        <div>
          <ColTitle>Contact</ColTitle>
          <ContactList>
            {ContactStore.filter(
              (item) => item.label === "Email Us" || item.label === "Call Us",
            ).map((item, i) => (
              <ContactItem key={i}>
                <Icon icon={item.icon} />
                <div>
                  {item.values.map((val, idx) => (
                    <ContactText key={idx}>{val}</ContactText>
                  ))}
                </div>
              </ContactItem>
            ))}
          </ContactList>
        </div>
      </Top>

      <Bottom>
        <Copy>
          © 2025 <span>Aura Metals</span> Co., Ltd. All rights reserved.
        </Copy>

        <BottomLinks>
          {bottomLinks.map((l, i) => (
            <React.Fragment key={l}>
              {i !== 0 && <Sep />}
              <a href="#">{l}</a>
            </React.Fragment>
          ))}
        </BottomLinks>

        <Right>
          <DevelopedByChip />
        </Right>
      </Bottom>
    </Wrapper>
  );
}

export default Footer;
